import { NodeType } from "../enums/node-type";
import { Voltage } from "../enums/voltage";
export class NetworkStructure {
  id!: number;
  class!: CimClass;
  genericName!: string;
  noOfEquipment!: number;
}

export class SubstationTemplateDto {
  id: number | null = null;
  className: string = '';
  voltageClass?: Voltage;
  visualizationConfiguration: VisualizationConfigurationDto;
  equipmentTemplates: EquipmentTemplateDto[];
  utilityId: number | null;

  constructor(data?: SubstationTemplateDto) {
    this.id = data?.id ?? null;
    this.utilityId = data?.utilityId ?? 0;
    this.className = data?.className ?? '';
    this.visualizationConfiguration = data?.visualizationConfiguration ?? new VisualizationConfigurationDto();
    this.equipmentTemplates = data?.equipmentTemplates ?? [];
  }
}

export class SubstationTemplateUICustomizationDto {
  id: number | null = null;
  className: string = '';
  voltageClass?: string;
}

export class EquipmentTemplateDto {
  id: number | null = null;
  guid: string | null = null;
  className: string = '';
  connectivities: ConnectivityDto[] = [];
  primaryVoltage: Voltage | null;
  secondaryVoltage: Voltage | null;
  voltage!: Voltage | null;
  visualizationConfiguration: VisualizationConfigurationDto;
  substationTemplateId: number | null = null;
  utilityId: number = 0;
  isNew: boolean = false;
  cimClass: CimClass;
  status: EquipmentStatus;

  constructor(data?: EquipmentTemplateDto) {
    this.id = data?.id ?? null;
    this.guid = data?.guid ?? null;
    this.className = data?.className ?? '';
    this.connectivities = data?.connectivities ?? [];
    this.primaryVoltage = data?.primaryVoltage ?? null;
    this.secondaryVoltage = data?.secondaryVoltage ?? null;
    this.visualizationConfiguration = data?.visualizationConfiguration ?? new VisualizationConfigurationDto();
    this.substationTemplateId = data?.substationTemplateId ?? null;
    this.utilityId = data?.utilityId ?? 0;
    this.isNew = data?.isNew ?? false;
    this.cimClass = data?.cimClass ?? CimClass.Substation;
    this.status = data?.status ?? EquipmentStatus.Pending;
  }
}

export class ConnectivityDto {
  guid: string | null;
  voltage: Voltage | null;
  utilityId: number = 0;
  name: string | null = null;
  nodeType: NodeType | null = null;
  equipmentTemplateId: number | null = null;

  constructor(data?: ConnectivityDto) {
    this.guid = data?.guid ?? null;
    this.voltage = data?.voltage ?? null;
    this.utilityId = data?.utilityId ?? 0;
    this.name = data?.name ?? null;
    this.nodeType = data?.nodeType ?? null;
    this.equipmentTemplateId = data?.equipmentTemplateId ?? null;
  }
}

export class VisualizationConfigurationDto {
  symbol: string | null;
  color: string | null;
  utilityId: number | null;

  constructor(data?: VisualizationConfigurationDto)
  {
    this.symbol = data?.symbol ?? null;
    this.color = data?.color ?? null;
    this.utilityId = data?.utilityId ?? 0;
  }
}

export enum CimClass {
  Substation = 0,
  PowerTransformer = 1,
  AcLineSegment = 2,
  SwitchGear = 3,
  Conductor = 4,
  Busbar = 5,
  Junction = 6,
  Jumper = 7,
  Fuse = 8,
  Disconnector = 9,
  GroundDisconnector = 10,
  LoadBreakSwitch = 11,
  Breaker = 12,
  Ground = 13,
  Recloser = 14,
  Cut = 15,
  Sectionaliser = 16,
  PetersenCoil = 17,
  ShuntCompensator = 18,
  FrequencyConverter = 19,
  StaticVarCompensator = 20
}

export const IconGroups: Record<CimClass, string[]> = {
  [CimClass.Substation]: ['Substation', 'Substation-1', 'Substation-2', 'Substation-3'],
  [CimClass.Conductor]: ['NodeEnergy-2'],
  [CimClass.PowerTransformer]: ['Transformer', 'Transformer-1', 'Transformer-2'],
  [CimClass.AcLineSegment]: ['Ac Line Segment', 'Ac Line Segment-1', 'Ac Line Segment-2'],
  [CimClass.SwitchGear]: ['Switch', 'Switch-1'],
  [CimClass.Busbar]: ['Busbar', 'Busbar-1'],
  [CimClass.Junction]: ['Junction-1', 'Junction-2'],
  [CimClass.Jumper]: ['Jumper', 'Jumper-1'],
  [CimClass.Fuse]: ['Fuse', 'Fuse-1', 'Fuse-2', 'Fuse-3', 'Fuse-4'],
  [CimClass.LoadBreakSwitch]: ['Load-Break-Switch', 'Load-Break-Switch-1'],
  [CimClass.Breaker]: ['Circuit-Breaker', 'Circuit-Breaker-1', 'Circuit-Breaker-2', 'Circuit-Breaker-3'],
  [CimClass.Disconnector]: ['Disconnector', 'Disconnector-1', 'Disconnector-2'],
  [CimClass.GroundDisconnector]: ['Ground Disconnector', 'Ground Disconnector-1', 'Ground Disconnector-2', 'Ground Disconnector-3'],
  [CimClass.Ground]: ['Placeholder'],
  [CimClass.Recloser]: ['Placeholder'],
  [CimClass.Cut]: ['Placeholder'],
  [CimClass.Sectionaliser]: ['Placeholder'],
  [CimClass.PetersenCoil]: ['Placeholder'],
  [CimClass.ShuntCompensator]: ['Placeholder'],
  [CimClass.FrequencyConverter]: ['Placeholder'],
  [CimClass.StaticVarCompensator]: ['Placeholder']
};

export enum EquipmentStatus {
  Pending = 0,
  Complete = 1
}
export class PowerTransformer {
  name!: string;
  primaryVoltage!: Voltage;
  secondaryVoltage!: Voltage;
  connectivitySource!: NetworkStructure;
  connectivityDestination!: NetworkStructure;
  symbol!: string;
  color!: string;
}
export class Switchgear {
  name!: string;
  voltage!: Voltage;
  connectivitySource!: NetworkStructure;
  connectivityDestination!: NetworkStructure;
  symbol!: string;
  color!: string;
}
export class Conductor {
  name!: string;
  voltage!: Voltage;
  connectivitySource!: NetworkStructure;
  connectivityDestination!: NetworkStructure;
  symbol!: string;
  color!: string;
}

export class ACLineSegment {
  name!: string;
  voltage!: Voltage;
  symbol!: string;
  color!: string;
}

export enum Connectivity {
  Busbar,
  CircuitBreaker
}

export enum PropertyType {
  String
}

export class SubstationTemplateForSelect {
  id: number | null = null;
  className: string = '';

  constructor(id: number, className: string)
  {
    this.id = id ?? null;
    this.className = className ?? '';
  }
}

export class AcLineCountForSubstation {
  incomingAcLinesCount: number = 0;
  outgoingAcLinesCount: number = 0;
}

export const EquipmentRules: Record<CimClass, {
  voltageLevels: string[];
  source: CimClass[];
  destination: CimClass[];
  constraints: string[];
}> = {
  [CimClass.AcLineSegment]: {
    voltageLevels: ['HV', 'MV', 'LV'],
    source: [],
    destination: [CimClass.Disconnector],
    constraints: [
      "Must connect into substation through Disconnector",
      "Cannot connect directly to Busbar or Transformer"
    ]
  },

  // [CimClass.Disconnector]: {
  //   voltageLevels: ['HV', 'MV', 'LV'],
  //   source: [CimClass.AcLineSegment],
  //   destination: [CimClass.CircuitBreaker],
  //   constraints: [
  //     "Cannot be placed downstream of a Circuit Breaker",
  //     "Used for isolation"
  //   ]
  // },
  // [CimClass.CircuitBreaker]: {
  //   voltageLevels: ['HV', 'MV'],
  //   source: [CimClass.Disconnector],
  //   destination: [CimClass.GroundDisconnector],
  //   constraints: [
  //     "Must be between Disconnector → Circuit Breaker → Ground Disconnector"
  //   ]
  // },
  // [CimClass.GroundDisconnector]: {
  //   voltageLevels: ['HV', 'MV', 'LV'],
  //   source: [CimClass.CircuitBreaker],
  //   destination: [CimClass.Jumper],
  //   constraints: [
  //     "Must connect immediately after Circuit Breaker"
  //   ]
  // },
  [CimClass.Jumper]: {
    voltageLevels: ['HV', 'MV', 'LV'],
    source: [CimClass.GroundDisconnector],
    destination: [CimClass.Junction],
    constraints: [
      "Jumper cannot connect directly to Busbar or Transformer"
    ]
  },

  [CimClass.Junction]: {
    voltageLevels: ['HV', 'MV', 'LV'],
    source: [CimClass.Jumper],
    destination: [CimClass.Fuse, CimClass.LoadBreakSwitch, CimClass.Busbar],
    constraints: [
      "Must have ≥2 outgoing connections"
    ]
  },

  [CimClass.Fuse]: {
    voltageLevels: ['HV', 'MV', 'LV'],
    source: [CimClass.Junction],
    destination: [CimClass.LoadBreakSwitch],
    constraints: [
      "Cannot connect directly to Busbar or Transformer"
    ]
  },

  [CimClass.LoadBreakSwitch]: {
    voltageLevels: ['HV', 'MV', 'LV'],
    source: [CimClass.Fuse, CimClass.Junction],
    destination: [CimClass.Busbar],
    constraints: [
      "Must feed into Busbar"
    ]
  },

  [CimClass.Busbar]: {
    voltageLevels: ['HV', 'MV', 'LV'],
    source: [CimClass.LoadBreakSwitch, CimClass.Junction],
    destination: [CimClass.PowerTransformer, CimClass.AcLineSegment],
    constraints: [
      "Central node for distribution"
    ]
  },

  [CimClass.PowerTransformer]: {
    voltageLevels: ['HV', 'MV'],
    source: [CimClass.Busbar],
    destination: [CimClass.AcLineSegment, CimClass.LoadBreakSwitch],
    constraints: [
      "Cannot connect directly to AC Line Segment without Busbar"
    ]
  },
  [CimClass.Substation]: {
    voltageLevels: [],
    source: [],
    destination: [],
    constraints: []
  },
  [CimClass.SwitchGear]: {
    voltageLevels: [],
    source: [],
    destination: [],
    constraints: []
  },
  [CimClass.Conductor]: {
    voltageLevels: [],
    source: [],
    destination: [],
    constraints: []
  },
  [CimClass.Breaker]: {
    voltageLevels: [],
    source: [],
    destination: [],
    constraints: []
  },
  [CimClass.Disconnector]: {
    voltageLevels: [],
    source: [],
    destination: [],
    constraints: []
  },
  [CimClass.GroundDisconnector]: {
    voltageLevels: [],
    source: [],
    destination: [],
    constraints: []
  },
  [CimClass.Ground]: {
    voltageLevels: [],
    source: [],
    destination: [],
    constraints: []
  },
  [CimClass.Recloser]: {
    voltageLevels: [],
    source: [],
    destination: [],
    constraints: []
  },
  [CimClass.Cut]: {
    voltageLevels: [],
    source: [],
    destination: [],
    constraints: []
  },
  [CimClass.Sectionaliser]: {
    voltageLevels: [],
    source: [],
    destination: [],
    constraints: []
  },
  [CimClass.PetersenCoil]: {
    voltageLevels: [],
    source: [],
    destination: [],
    constraints: []
  },
  [CimClass.ShuntCompensator]: {
    voltageLevels: [],
    source: [],
    destination: [],
    constraints: []
  },
  [CimClass.FrequencyConverter]: {
    voltageLevels: [],
    source: [],
    destination: [],
    constraints: []
  },
  [CimClass.StaticVarCompensator]: {
    voltageLevels: [],
    source: [],
    destination: [],
    constraints: []
  }
};